import { getIdToken } from './firebase';

/**
 * API レスポンスの基本型
 */
interface ApiResponse<T> {
  data: T;
  status: number;
}

/**
 * API エラー
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

/**
 * 認証済み API クライアント
 *
 * - Bearer トークンを Authorization ヘッダーに自動付与
 * - admin-console-api の /api/v1/* エンドポイントにリクエスト
 * - 開発環境では Vite の proxy 経由で admin-console-api に転送
 */
class ApiClient {
  private baseUrl: string;

  constructor() {
    // 開発環境: Vite proxy 経由（同一オリジン）
    // 本番環境: 同一ドメイン（admin.caream.jp）
    this.baseUrl = '/api/v1';
  }

  /**
   * 認証ヘッダー付きの fetch を実行
   */
  private async request<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const token = await getIdToken();

    if (!token) {
      throw new ApiError(401, '認証トークンが取得できません');
    }

    const url = `${this.baseUrl}${path}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ApiError(response.status, response.statusText, body);
    }

    const data = (await response.json()) as T;
    return { data, status: response.status };
  }

  /**
   * GET リクエスト
   *
   * params を渡した場合は URLSearchParams でシリアライズしてクエリ文字列化する。
   * 値が undefined / null / '' の要素は送出しない。
   */
  async get<T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined | null>
  ): Promise<ApiResponse<T>> {
    let finalPath = path;
    if (params) {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') continue;
        search.set(key, String(value));
      }
      const qs = search.toString();
      if (qs) finalPath = `${path}?${qs}`;
    }
    return this.request<T>(finalPath, { method: 'GET' });
  }

  /**
   * POST リクエスト
   */
  async post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PUT リクエスト
   */
  async put<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE リクエスト
   */
  async delete<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>(path, { method: 'DELETE' });
  }
}

/**
 * API クライアントのシングルトンインスタンス
 */
export const apiClient = new ApiClient();

/**
 * 認証済みユーザー情報の型（/api/v1/auth/me レスポンス）
 */
export interface AuthMeResponse {
  uid: string;
  email: string;
  tenantId: string;
  profile: {
    id: string;
    displayName: string;
    email: string;
    createdAt: string;
    updatedAt: string;
  } | null;
}

/**
 * 認証済みユーザー情報を取得
 */
export async function fetchAuthMe(): Promise<AuthMeResponse> {
  const { data } = await apiClient.get<AuthMeResponse>('/auth/me');
  return data;
}
